"use client"

import { useEffect, useRef } from "react"
import "./actions-section.css"

/**
 * "Actions" section — port of the Claude Design export
 * `Actions Section v2.dc.html` (# Velova Actions Section (1)). The "ACT"
 * beat of the operating loop: one detected gap, three ways Velova prepares
 * the fix (Website & SEO / GEO & AEO / Sources & authority — radio-
 * selectable), a human-review bar, and the proof rail.
 *
 * Sits on the homepage right after the Key Features bento. That section
 * already closes with the bridge line "Next, Velova shows you what to do." —
 * the export repeats that same bridge as its opener, so it is intentionally
 * dropped here to avoid printing it twice.
 *
 * Styling lives in the scoped ./actions-section.css (fonts remapped to the
 * app's next/font CSS variables — no new font requests). The DCLogic
 * entrance + radio-select animation is ported below into a useEffect: it
 * only mutates opacity/transform inline against the `data-a2` hooks, and
 * re-arms on scroll-out. The selected pillar's visual state is declarative
 * in CSS via [aria-checked]; the effect just toggles the attribute.
 * prefers-reduced-motion keeps the finished state (radios still work).
 *
 * The aria-hidden micro-glyphs inside each row/tick are one-off decorations
 * with no font or pseudo-state, so they stay as inline geometry here.
 */

const GOLD = "#E4CBA2"
const ACCENT = "var(--a2-accent)"

const EVIDENCE = ["12 questions affected", "2 competitors ahead", "3 source gaps"] as const

type Row = { glyph: React.ReactNode; text: string; tag: string; gold?: boolean }
type Work = { tick: React.ReactNode; text: string }
type Pillar = {
  title: string
  found: string
  count: string
  countTo: number
  iconStyle: React.CSSProperties
  iconInner: React.ReactNode
  rows: Row[]
  more: string
  workLabel: string
  workItems: Work[]
}

/* ── decorative glyph boxes (aria-hidden, inline geometry) ── */
const ROW_BOX: React.CSSProperties = { width: "15px", height: "15px", borderRadius: "4px" }
const NEUTRAL = { border: "1px solid rgba(160,196,224,.28)", background: "rgba(120,170,210,.05)" }
const PULSE = { border: "1px solid rgba(228,203,162,.5)", background: "rgba(228,203,162,.09)" }

const PILLARS: Pillar[] = [
  {
    title: "Website & SEO",
    found: "Issues found",
    count: "14",
    countTo: 14,
    iconStyle: { display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px", padding: "0 4px" },
    iconInner: (
      <>
        <span style={{ display: "block", height: "2px", borderRadius: "1px", background: "rgba(207,238,247,.75)" }} />
        <span style={{ display: "block", width: "70%", height: "1px", background: "rgba(174,208,232,.5)" }} />
        <span style={{ display: "block", width: "45%", height: "1px", background: "rgba(174,208,232,.35)" }} />
      </>
    ),
    rows: [
      {
        text: "Missing H1 on the product page",
        tag: "Critical",
        gold: true,
        glyph: (
          <span data-a2="glyph" aria-hidden className="a2-glyph-pulse" style={{ ...ROW_BOX, ...PULSE, display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px", padding: "0 3px" }}>
            <span style={{ display: "block", height: "2px", borderRadius: "1px", background: GOLD }} />
            <span style={{ display: "block", width: "60%", height: "1px", background: "rgba(228,203,162,.45)" }} />
          </span>
        ),
      },
      {
        text: "Duplicate page title",
        tag: "High",
        glyph: (
          <span data-a2="glyph" aria-hidden style={{ ...ROW_BOX, ...NEUTRAL, position: "relative" }}>
            <span style={{ position: "absolute", left: "3px", top: "3px", width: "6px", height: "6px", border: "1px solid rgba(174,208,232,.75)" }} />
            <span style={{ position: "absolute", left: "6px", top: "6px", width: "6px", height: "6px", border: "1px solid rgba(174,208,232,.4)", background: "#04070C" }} />
          </span>
        ),
      },
      {
        text: "Broken internal links",
        tag: "Medium",
        glyph: (
          <span data-a2="glyph" aria-hidden style={{ ...ROW_BOX, ...NEUTRAL, display: "flex", alignItems: "center", justifyContent: "center", gap: "1px" }}>
            <span style={{ width: "4px", height: "4px", borderRadius: "1px", background: "rgba(174,208,232,.7)" }} />
            <span style={{ width: "3px", height: "1px", borderTop: "1px dashed rgba(174,208,232,.85)" }} />
            <span style={{ width: "4px", height: "4px", borderRadius: "1px", border: "1px solid rgba(174,208,232,.45)" }} />
          </span>
        ),
      },
      {
        text: "Product schema missing",
        tag: "Missing",
        gold: true,
        glyph: (
          <span data-a2="glyph" aria-hidden className="a2-glyph-pulse" style={{ ...ROW_BOX, border: "1px dashed rgba(228,203,162,.55)", background: "rgba(228,203,162,.07)", display: "flex", flexWrap: "wrap", alignContent: "center", justifyContent: "center", gap: "2px", padding: "0 2px" }}>
            <span style={{ width: "3px", height: "3px", background: GOLD }} />
            <span style={{ width: "3px", height: "3px", background: "rgba(228,203,162,.5)" }} />
            <span style={{ width: "3px", height: "3px", background: "rgba(228,203,162,.5)" }} />
            <span style={{ width: "3px", height: "3px", border: "1px solid rgba(228,203,162,.5)" }} />
          </span>
        ),
      },
    ],
    more: "+10 more website issues",
    workLabel: "Fix prepared",
    workItems: [
      {
        text: "New title and H1",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "3px", padding: "0 4px" }}>
            <span style={{ display: "block", width: "100%", height: "3px", borderRadius: "1px", background: ACCENT }} />
            <span style={{ display: "block", width: "62%", height: "2px", borderRadius: "1px", background: "rgba(207,238,247,.4)" }} />
          </span>
        ),
      },
      {
        text: "Internal-link map",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: ACCENT }} />
            <span style={{ width: "5px", height: "1px", background: ACCENT }} />
            <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: ACCENT }} />
            <span style={{ width: "5px", height: "1px", background: "rgba(178,224,240,.5)" }} />
            <span style={{ width: "4px", height: "4px", borderRadius: "50%", border: "1px solid rgba(178,224,240,.7)" }} />
          </span>
        ),
      },
      {
        text: "Product schema markup",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", flexWrap: "wrap", alignContent: "center", justifyContent: "center", gap: "2px", padding: "0 4px" }}>
            <span style={{ width: "4px", height: "4px", background: ACCENT }} />
            <span style={{ width: "4px", height: "4px", background: "rgba(178,224,240,.7)" }} />
            <span style={{ width: "4px", height: "4px", background: "rgba(178,224,240,.7)" }} />
            <span style={{ width: "4px", height: "4px", background: "rgba(178,224,240,.45)" }} />
          </span>
        ),
      },
    ],
  },
  {
    title: "GEO & AEO",
    found: "Issues found",
    count: "8",
    countTo: 8,
    iconStyle: { display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" },
    iconInner: (
      <>
        <span style={{ width: "4px", height: "4px", borderRadius: "1px", background: "rgba(207,238,247,.65)" }} />
        <span style={{ width: "4px", height: "4px", borderRadius: "1px", background: "rgba(207,238,247,.4)" }} />
        <span className="a2-glyph-slot" style={{ width: "5px", height: "5px", borderRadius: "1px", border: `1px dashed ${ACCENT}` }} />
      </>
    ),
    rows: [
      {
        text: "ChatGPT search crawler blocked",
        tag: "Blocked",
        gold: true,
        glyph: (
          <span data-a2="glyph" aria-hidden className="a2-glyph-pulse" style={{ ...ROW_BOX, ...PULSE, position: "relative" }}>
            <span style={{ position: "absolute", left: "3px", top: "3px", width: "7px", height: "7px", borderRadius: "50%", border: `1px solid ${GOLD}` }} />
            <span style={{ position: "absolute", left: "1px", top: "6px", width: "11px", height: "1px", background: GOLD, transform: "rotate(-45deg)" }} />
          </span>
        ),
      },
      {
        text: "12 buyer questions unanswered",
        tag: "High",
        glyph: (
          <span data-a2="glyph" aria-hidden style={{ ...ROW_BOX, ...NEUTRAL, display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px", padding: "0 3px" }}>
            <span style={{ display: "block", height: "1px", background: "rgba(174,208,232,.7)" }} />
            <span style={{ display: "block", width: "70%", height: "1px", background: "rgba(174,208,232,.5)" }} />
            <span style={{ display: "block", width: "45%", height: "1px", borderTop: "1px dashed rgba(174,208,232,.8)" }} />
          </span>
        ),
      },
      {
        text: "Sleep-accuracy claims lack proof",
        tag: "Missing",
        gold: true,
        glyph: (
          <span data-a2="glyph" aria-hidden className="a2-glyph-pulse" style={{ ...ROW_BOX, ...PULSE, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: "7px", height: "7px", border: `1px solid ${GOLD}`, transform: "rotate(45deg)" }} />
          </span>
        ),
      },
      {
        text: "Important answer buried on the page",
        tag: "Medium",
        glyph: (
          <span data-a2="glyph" aria-hidden style={{ ...ROW_BOX, ...NEUTRAL, display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px", padding: "0 3px" }}>
            <span style={{ display: "block", height: "1px", background: "rgba(174,208,232,.35)" }} />
            <span style={{ display: "block", height: "1px", background: "rgba(174,208,232,.35)" }} />
            <span style={{ display: "block", height: "2px", borderRadius: "1px", background: "rgba(174,208,232,.9)" }} />
          </span>
        ),
      },
    ],
    more: "+4 more answer-readiness gaps",
    workLabel: "Content prepared",
    workItems: [
      {
        text: "Comparison-page brief",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "2px", paddingBottom: "4px" }}>
            <span style={{ width: "4px", height: "11px", borderRadius: "1px", background: ACCENT }} />
            <span style={{ width: "4px", height: "8px", borderRadius: "1px", background: "rgba(178,224,240,.6)" }} />
            <span style={{ width: "4px", height: "5px", borderRadius: "1px", background: "rgba(178,224,240,.35)" }} />
          </span>
        ),
      },
      {
        text: "Answers for 12 missed questions",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px", padding: "0 4px" }}>
            <span style={{ display: "block", width: "100%", height: "2px", borderRadius: "1px", background: ACCENT }} />
            <span style={{ display: "block", width: "72%", height: "2px", borderRadius: "1px", background: "rgba(178,224,240,.55)" }} />
            <span style={{ display: "block", width: "88%", height: "2px", borderRadius: "1px", background: "rgba(207,238,247,.4)" }} />
          </span>
        ),
      },
      {
        text: "Proof and claims checklist",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: "8px", height: "4px", borderLeft: `1.5px solid ${ACCENT}`, borderBottom: `1.5px solid ${ACCENT}`, transform: "rotate(-45deg)", marginTop: "-2px" }} />
          </span>
        ),
      },
    ],
  },
  {
    title: "Sources & authority",
    found: "Gaps found",
    count: "6",
    countTo: 6,
    iconStyle: { display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" },
    iconInner: (
      <>
        <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(207,238,247,.7)" }} />
        <span style={{ width: "5px", height: "1px", background: "rgba(174,208,232,.45)" }} />
        <span style={{ width: "4px", height: "4px", borderRadius: "50%", border: "1px solid rgba(174,208,232,.65)" }} />
      </>
    ),
    rows: [
      {
        text: "Missing from a frequently cited roundup",
        tag: "High",
        glyph: (
          <span data-a2="glyph" aria-hidden style={{ ...ROW_BOX, ...NEUTRAL, display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
            <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(174,208,232,.75)" }} />
            <span style={{ width: "3px", height: "3px", borderRadius: "50%", border: "1px dashed rgba(228,203,162,.9)" }} />
            <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(174,208,232,.45)" }} />
          </span>
        ),
      },
      {
        text: "Two review profiles incomplete",
        tag: "Medium",
        glyph: (
          <span data-a2="glyph" aria-hidden style={{ ...ROW_BOX, ...NEUTRAL, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", border: "1px solid rgba(174,208,232,.8)", borderRightColor: "transparent" }} />
          </span>
        ),
      },
      {
        text: "Product information outdated on a directory",
        tag: "Outdated",
        gold: true,
        glyph: (
          <span data-a2="glyph" aria-hidden className="a2-glyph-pulse" style={{ ...ROW_BOX, ...PULSE, position: "relative" }}>
            <span style={{ position: "absolute", left: "3px", top: "3px", width: "7px", height: "7px", borderRadius: "50%", border: `1px solid ${GOLD}` }} />
            <span style={{ position: "absolute", left: "6px", top: "4px", width: "1px", height: "3px", background: GOLD }} />
          </span>
        ),
      },
      {
        text: "Competitors dominate three cited sources",
        tag: "Critical",
        gold: true,
        glyph: (
          <span data-a2="glyph" aria-hidden className="a2-glyph-pulse" style={{ ...ROW_BOX, ...PULSE, display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "2px", paddingBottom: "3px" }}>
            <span style={{ width: "2px", height: "3px", background: "rgba(228,203,162,.4)" }} />
            <span style={{ width: "2px", height: "6px", background: "rgba(228,203,162,.7)" }} />
            <span style={{ width: "2px", height: "8px", background: GOLD }} />
          </span>
        ),
      },
    ],
    more: "+2 more external source gaps",
    workLabel: "Source work prepared",
    workItems: [
      {
        text: "Roundup inclusion brief",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
            <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(178,224,240,.55)" }} />
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: ACCENT }} />
            <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(178,224,240,.55)" }} />
          </span>
        ),
      },
      {
        text: "Profile-completion checklist",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ width: "11px", height: "11px", borderRadius: "50%", border: `1.5px solid ${ACCENT}` }} />
          </span>
        ),
      },
      {
        text: "Factual-correction request",
        tick: (
          <span data-a2="tick" aria-hidden className="a2-tick" style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "3px" }}>
            <span style={{ width: "11px", height: "2px", borderRadius: "1px", background: ACCENT }} />
            <span style={{ width: "7px", height: "2px", borderRadius: "1px", background: "rgba(178,224,240,.45)" }} />
          </span>
        ),
      },
    ],
  },
]

type Proof = { title: string; desc: string; glyphStyle: React.CSSProperties; glyphInner: React.ReactNode }
const PROOF: Proof[] = [
  {
    title: "Actual AI answers",
    desc: "The questions and responses that triggered the Action.",
    glyphStyle: { display: "flex", flexDirection: "column", justifyContent: "center", gap: "3px", padding: "0 6px" },
    glyphInner: (
      <>
        <span style={{ display: "block", height: "2px", borderRadius: "1px", background: "rgba(207,238,247,.85)" }} />
        <span style={{ display: "block", width: "70%", height: "1px", background: "rgba(207,238,247,.5)" }} />
        <span style={{ display: "block", width: "88%", height: "1px", background: "rgba(207,238,247,.35)" }} />
        <span style={{ display: "block", width: "46%", height: "1px", background: "rgba(207,238,247,.25)" }} />
      </>
    ),
  },
  {
    title: "Visible evidence",
    desc: "The competitors, sources and website issues behind it.",
    glyphStyle: { display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "3px", paddingBottom: "6px" },
    glyphInner: (
      <>
        <span style={{ width: "3px", height: "6px", borderRadius: "1px", background: "rgba(207,238,247,.4)" }} />
        <span style={{ width: "3px", height: "11px", borderRadius: "1px", background: "rgba(228,203,162,.9)" }} />
        <span style={{ width: "3px", height: "8px", borderRadius: "1px", background: "rgba(207,238,247,.55)" }} />
        <span style={{ width: "3px", height: "5px", borderRadius: "1px", background: "rgba(207,238,247,.3)" }} />
      </>
    ),
  },
  {
    title: "Human approval",
    desc: "Review and edit every prepared fix before implementation.",
    glyphStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    glyphInner: (
      <span style={{ position: "relative", width: "18px", height: "20px", borderRadius: "3px", border: "1px solid rgba(143,217,196,.55)", background: "rgba(143,217,196,.08)" }}>
        <span style={{ position: "absolute", left: "4px", top: "7px", width: "8px", height: "4px", borderLeft: "1.5px solid #8FD9C4", borderBottom: "1.5px solid #8FD9C4", transform: "rotate(-45deg)" }} />
      </span>
    ),
  },
  {
    title: "Remeasurement",
    desc: "Run the affected questions again after the work goes live.",
    glyphStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    glyphInner: (
      <span className="a2-spin" style={{ width: "17px", height: "17px", borderRadius: "50%", border: "1px solid rgba(207,238,247,.75)", borderTopColor: "transparent" }} />
    ),
  },
]

const SELECTED_INDEX = 1

function PillarCard({ p, selected }: { p: Pillar; selected: boolean }) {
  return (
    <div
      data-a2="pillar"
      role="radio"
      aria-checked={selected ? "true" : "false"}
      aria-label={p.title}
      tabIndex={selected ? 0 : -1}
      className="a2-pillar"
    >
      <div className="a2-pillar-head">
        <span aria-hidden className="a2-pillar-icon" style={p.iconStyle}>
          {p.iconInner}
        </span>
        <span className="a2-title">{p.title}</span>
        <span className="a2-chip-off">Select</span>
        <span className="a2-chip-on">
          <span aria-hidden className="a2-chip-on-dot" />
          Selected
        </span>
      </div>

      <div className="a2-found-row">
        <span className="a2-found">{p.found}</span>
        <span aria-hidden className="a2-found-line" />
        <strong data-a2="count" data-to={p.countTo} className="a2-count">
          {p.count}
        </strong>
      </div>

      <div className="a2-rows">
        {p.rows.map((r, j) => (
          <div key={j} data-a2="row" className="a2-row">
            {r.glyph}
            <span className="a2-row-text">{r.text}</span>
            <span className={`a2-row-tag ${r.gold ? "a2-row-tag-gold" : "a2-row-tag-mute"}`}>{r.tag}</span>
          </div>
        ))}
      </div>

      <span className="a2-more">{p.more}</span>

      <div className="a2-work">
        <div className="a2-work-box">
          <span className="a2-work-head">
            <span className="a2-work-label">{p.workLabel}</span>
            <span className="a2-work-count"> · 3 items</span>
          </span>
          <div className="a2-work-list">
            {p.workItems.map((w, j) => (
              <span key={j} data-a2="work-row" className="a2-work-row">
                {w.tick}
                {w.text}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function ActionsSection() {
  const rootRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const q = (sel: string, scope: ParentNode = root) =>
      Array.from(scope.querySelectorAll<HTMLElement>(sel))
    const set = (el: HTMLElement | null | undefined, p: Record<string, string>) => {
      if (el) Object.assign(el.style, p)
    }
    const setAll = (list: HTMLElement[], p: Record<string, string>) => list.forEach((el) => set(el, p))

    let timers: number[] = []
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms))
    const clearTimers = () => {
      timers.forEach(clearTimeout)
      timers = []
    }
    let armed = false
    let played = false
    let countRaf = 0

    const cards = q('[data-a2="pillar"]')
    let selected = SELECTED_INDEX
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const countUp = (el: HTMLElement) => {
      const to = parseInt(el.getAttribute("data-to") || "", 10)
      if (!to) return
      const dur = 780
      const t0 = performance.now()
      const step = (now: number) => {
        const p = Math.min(1, (now - t0) / dur)
        el.textContent = String(Math.round(to * (1 - Math.pow(1 - p, 3))))
        if (p < 1) countRaf = requestAnimationFrame(step)
      }
      el.textContent = "0"
      countRaf = requestAnimationFrame(step)
    }

    // glyph + issue row, then prepared work — each on its own stagger
    const revealCard = (card: HTMLElement, base: number) => {
      q('[data-a2="row"]', card).forEach((el, i) => {
        const g = q('[data-a2="glyph"]', el)[0]
        set(el, { transition: "none", opacity: "0", transform: "translateX(-8px)" })
        set(g, { transition: "none", opacity: "0", transform: "scale(.5)" })
        at(base + 60 + i * 70, () => {
          set(el, { transition: "opacity .42s ease, transform .5s cubic-bezier(.2,.7,.2,1), background .25s ease", opacity: "1", transform: "none" })
          set(g, { transition: "opacity .3s ease, transform .45s cubic-bezier(.2,.9,.3,1)", opacity: "1", transform: "none" })
        })
      })
      q('[data-a2="work-row"]', card).forEach((el, i) => {
        const t = q('[data-a2="tick"]', el)[0]
        set(el, { transition: "none", opacity: "0", transform: "translateY(7px)" })
        set(t, { transition: "none", transform: "scale(.4)" })
        at(base + 320 + i * 90, () => {
          set(el, { transition: "opacity .4s ease, transform .45s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" })
          set(t, { transition: "transform .45s cubic-bezier(.2,.9,.3,1)", transform: "none" })
        })
      })
    }

    const arm = () => {
      set(q('[data-a2="header"]')[0], { transition: "none", opacity: "0", transform: "translateY(18px)" })
      set(q('[data-a2="rule"]')[0], { transition: "none", transform: "scaleX(0)" })
      set(q('[data-a2="panel"]')[0], { transition: "none", opacity: "0", transform: "translateY(22px) scale(.985)" })
      setAll(q('[data-a2="step"]'), { transition: "none", opacity: "0", transform: "translateY(6px)" })
      setAll(q('[data-a2="evidence"]'), { transition: "none", opacity: "0", transform: "scale(.94)" })
      setAll(cards, { opacity: "0" })
      setAll(q('[data-a2="row"]'), { transition: "none", opacity: "0", transform: "translateX(-8px)" })
      setAll(q('[data-a2="glyph"]'), { transition: "none", opacity: "0", transform: "scale(.5)" })
      setAll(q('[data-a2="work-row"]'), { transition: "none", opacity: "0", transform: "translateY(7px)" })
      set(q('[data-a2="gap-viz"]')[0], { transition: "none", opacity: "0", transform: "scale(.94)" })
      set(q('[data-a2="proof-rule"]')[0], { transition: "none", transform: "scaleX(0)" })
      setAll(q('[data-a2="tick"]'), { transition: "none", transform: "scale(.4)" })
      setAll(q('[data-a2="count"]'), { transition: "none", opacity: "0", transform: "translateY(6px)" })
      set(q('[data-a2="review"]')[0], { transition: "none", opacity: "0", transform: "translateY(10px)" })
      set(q('[data-a2="proof"]')[0], { transition: "none", opacity: "0" })
      setAll(q('[data-a2="proof-cell"]'), { transition: "none", opacity: "0", transform: "translateY(8px)" })
      armed = true
    }

    const play = () => {
      if (!armed || played) return
      played = true
      set(q('[data-a2="header"]')[0], { transition: "opacity .6s ease, transform .7s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" })
      at(120, () => set(q('[data-a2="rule"]')[0], { transition: "transform .8s cubic-bezier(.2,.7,.2,1)", transform: "scaleX(1)" }))
      q('[data-a2="step"]').forEach((el, i) =>
        at(180 + i * 90, () => set(el, { transition: "opacity .45s ease, transform .5s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" })),
      )
      at(180, () => set(q('[data-a2="panel"]')[0], { transition: "opacity .7s ease, transform .8s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }))
      at(420, () => set(q('[data-a2="gap-viz"]')[0], { transition: "opacity .5s ease, transform .6s cubic-bezier(.2,.9,.3,1)", opacity: "1", transform: "none" }))
      at(1360, () => set(q('[data-a2="proof-rule"]')[0], { transition: "transform .9s cubic-bezier(.2,.7,.2,1)", transform: "scaleX(1)" }))
      q('[data-a2="evidence"]').forEach((el, i) =>
        at(520 + i * 90, () => set(el, { transition: "opacity .4s ease, transform .45s cubic-bezier(.2,.9,.3,1)", opacity: "1", transform: "none" })),
      )

      const delays = [280, 180, 380]
      cards.forEach((el, i) => {
        set(el, { transition: "none", transform: "translateY(16px)" })
        at(360 + delays[i], () =>
          set(el, { transition: "opacity .55s ease, transform .6s cubic-bezier(.2,.7,.2,1), border-color .3s ease, box-shadow .3s ease", opacity: "1", transform: "none" }),
        )
        at(560 + delays[i], () => {
          const c = q('[data-a2="count"]', el)[0]
          set(c, { transition: "opacity .35s ease, transform .45s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" })
          if (c) countUp(c)
        })
        at(620 + delays[i], () => revealCard(el, 0))
        // hand transform/opacity/transition back to CSS so :hover can take over
        at(1360 + delays[i], () => set(el, { transition: "", transform: "", opacity: "" }))
      })

      at(1180, () => set(q('[data-a2="review"]')[0], { transition: "opacity .6s ease, transform .7s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }))
      at(1320, () => set(q('[data-a2="proof"]')[0], { transition: "opacity .6s ease", opacity: "1" }))
      q('[data-a2="proof-cell"]').forEach((el, i) => {
        at(1400 + i * 90, () => set(el, { transition: "opacity .45s ease, transform .5s cubic-bezier(.2,.7,.2,1)", opacity: "1", transform: "none" }))
        at(2000 + i * 90, () => set(el, { transition: "", transform: "", opacity: "" }))
      })
    }

    // leaving the viewport rearms the entrance so it replays on return
    const reset = () => {
      clearTimers()
      played = false
      arm()
    }

    // ---- radio wiring (works even under reduced motion) ----
    const select = (i: number, focus?: boolean) => {
      if (i === selected) {
        if (focus) cards[i]?.focus()
        return
      }
      selected = i
      cards.forEach((card, j) => {
        const on = j === i
        card.setAttribute("aria-checked", on ? "true" : "false")
        card.tabIndex = on ? 0 : -1
      })
      if (!reduced) revealCard(cards[i], 0)
      if (focus) cards[i]?.focus()
    }

    const removers: (() => void)[] = []
    cards.forEach((card, i) => {
      const onClick = () => select(i)
      const onKey = (ev: KeyboardEvent) => {
        const k = ev.key
        if (k === "ArrowRight" || k === "ArrowDown") {
          ev.preventDefault()
          select((i + 1) % cards.length, true)
        } else if (k === "ArrowLeft" || k === "ArrowUp") {
          ev.preventDefault()
          select((i - 1 + cards.length) % cards.length, true)
        } else if (k === " " || k === "Enter") {
          ev.preventDefault()
          select(i, true)
        }
      }
      card.addEventListener("click", onClick)
      card.addEventListener("keydown", onKey)
      removers.push(() => {
        card.removeEventListener("click", onClick)
        card.removeEventListener("keydown", onKey)
      })
    })

    if (reduced) {
      return () => removers.forEach((fn) => fn())
    }

    arm()
    let io: IntersectionObserver | null = null
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (e) => {
          if (e[0].isIntersecting) play()
          else if (played) reset()
        },
        { threshold: 0.08, rootMargin: "0px 2400px 0px 2400px" },
      )
      io.observe(root)
    } else {
      play()
    }
    // safety: reveal even if the observer never fires
    at(2600, () => play())

    return () => {
      clearTimers()
      if (countRaf) cancelAnimationFrame(countRaf)
      if (io) io.disconnect()
      removers.forEach((fn) => fn())
    }
  }, [])

  return (
    <section id="actions" ref={rootRef} aria-labelledby="a2-headline" className="velova-actions section-default">
      <div aria-hidden className="a2-glow" />

      <div className="velova-container">

      <header data-a2="header" className="a2-header">
        <div className="a2-head-left">
          <div className="a2-eyebrow">
            <span aria-hidden className="a2-gem" />
            <span className="a2-eyebrow-label">Actions</span>
            <span data-a2="rule" aria-hidden className="a2-rule" />
          </div>
          <h2 id="a2-headline" className="a2-headline">
            One gap. <span className="a2-headline-soft">Three ways Velova fixes it.</span>
          </h2>
        </div>

        <div className="a2-steps">
          <span data-a2="step" className="a2-step a2-step-gap">Gap found</span>
          <span aria-hidden className="a2-step-conn">
            <span data-a2="travel" className="a2-travel" />
          </span>
          <span data-a2="step" className="a2-step a2-step-fix">Fixes prepared</span>
          <span aria-hidden className="a2-step-conn">
            <span data-a2="travel" className="a2-travel" />
          </span>
          <span data-a2="step" className="a2-step a2-step-review">Team review</span>
        </div>
      </header>

      <div data-a2="panel" className="a2-panel">
        <span aria-hidden className="a2-topline">
          <span aria-hidden className="a2-shine" />
        </span>

        {/* Gap found */}
        <div className="a2-gapbar">
          <span aria-hidden className="a2-sweep" />
          <span data-a2="gap-viz" aria-hidden className="a2-gapviz">
            <span className="a2-gapviz-bar" style={{ width: "20px", background: "rgba(174,208,232,.45)" }} />
            <span className="a2-gapviz-bar" style={{ width: "15px", background: "rgba(174,208,232,.3)" }} />
            <span className="a2-gapviz-bar a2-gapviz-miss" style={{ width: "18px" }} />
          </span>
          <span className="a2-gap-label">Gap found</span>
          <p className="a2-gap-text">
            Buyers ask which tracker is best for sleep. Your brand is not in the answer.
          </p>
          <span className="a2-evidence-wrap">
            {EVIDENCE.map((t) => (
              <span key={t} data-a2="evidence" className="a2-evidence">
                {t}
              </span>
            ))}
          </span>
        </div>

        {/* Three fix approaches */}
        <div role="radiogroup" aria-label="Ways to fix this gap" className="a2-pillars">
          {PILLARS.map((p, i) => (
            <PillarCard key={p.title} p={p} selected={i === SELECTED_INDEX} />
          ))}
        </div>

        {/* Ready for review */}
        <div data-a2="review" className="a2-review">
          <span aria-hidden className="a2-review-check">
            <span className="a2-review-ring" />
            <span className="a2-review-tick" />
          </span>
          <span className="a2-review-copy">
            <span className="a2-review-eyebrow">Ready for review</span>
            <span className="a2-review-title">Review, edit and approve the prepared work.</span>
            <span className="a2-review-sub">
              Nothing goes live until you approve it — then the same 12 questions are measured again.
            </span>
          </span>
          <span aria-hidden className="a2-sheets">
            <span className="a2-sheet a2-sheet-1" />
            <span className="a2-sheet a2-sheet-2" />
            <span className="a2-sheet a2-sheet-3">
              <span className="a2-sheet-line" style={{ background: "rgba(207,238,247,.5)" }} />
              <span className="a2-sheet-line" style={{ width: "68%", background: "rgba(207,238,247,.3)" }} />
              <span className="a2-sheet-line" style={{ width: "44%", background: "rgba(143,217,196,.85)" }} />
            </span>
          </span>
        </div>

        {/* Proof rail */}
        <div data-a2="proof" className="a2-proof">
          <div className="a2-proof-head">
            <span className="a2-proof-label">Every action is backed by</span>
            <span data-a2="proof-rule" aria-hidden className="a2-proof-rule" />
          </div>
          <div className="a2-proof-grid">
            {PROOF.map((c) => (
              <div key={c.title} data-a2="proof-cell" className="a2-proof-cell">
                <span aria-hidden className="a2-proof-glyph" style={c.glyphStyle}>
                  {c.glyphInner}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="a2-proof-title">{c.title}</span>
                  <span className="a2-proof-desc">{c.desc}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </section>
  )
}
